import React, { useState, useEffect } from 'react';
import './SnapshotEditForm.css';

const SnapshotEditForm = ({ snapshot, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    date: '',
    totalValue: '',
    totalAssets: '',
    totalLiabilities: '',
    netWorth: '',
    currency: 'EUR',
    containsStaleData: false,
    staleCategories: [],
    // Category values
    stocks: '',
    crypto: '',
    preciousMetals: '',
    cash: '',
    realEstate: '',
    liabilities: '',
    // Benchmark data
    sp500Value: '',
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (snapshot) {
      setFormData({
        date: snapshot.date || '',
        totalValue: snapshot.totalValue || '',
        totalAssets: snapshot.totalAssets || '',
        totalLiabilities: snapshot.totalLiabilities || '',
        netWorth: snapshot.netWorth || '',
        currency: snapshot.currency || 'EUR',
        containsStaleData: snapshot.containsStaleData || false,
        staleCategories: snapshot.staleCategories || [],
        // Extract category values
        stocks: snapshot.categories?.stocks?.value || '',
        crypto: snapshot.categories?.crypto?.value || '',
        preciousMetals: snapshot.categories?.preciousMetals?.value || '',
        cash: snapshot.categories?.cash?.value || '',
        realEstate: snapshot.categories?.realEstate?.value || '',
        liabilities: snapshot.categories?.liabilities?.value || '',
        // Benchmark data
        sp500Value: snapshot.sp500Value || '',
      });
    }
  }, [snapshot]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};

    // Validate numeric fields
    const numericFields = [
      'totalValue', 'totalAssets', 'totalLiabilities', 'netWorth',
      'stocks', 'crypto', 'preciousMetals', 'cash', 'realEstate', 'liabilities',
      'sp500Value'
    ];

    numericFields.forEach(field => {
      if (formData[field] !== '' && isNaN(parseFloat(formData[field]))) {
        newErrors[field] = 'Must be a valid number';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSaving(true);

    try {
      // Build categories object
      const categories = {
        stocks: {
          value: parseFloat(formData.stocks) || 0,
          percentage: snapshot.categories?.stocks?.percentage || 0,
          count: snapshot.categories?.stocks?.count || 0,
          lastUpdated: snapshot.categories?.stocks?.lastUpdated || null,
        },
        crypto: {
          value: parseFloat(formData.crypto) || 0,
          percentage: snapshot.categories?.crypto?.percentage || 0,
          count: snapshot.categories?.crypto?.count || 0,
          lastUpdated: snapshot.categories?.crypto?.lastUpdated || null,
        },
        preciousMetals: {
          value: parseFloat(formData.preciousMetals) || 0,
          percentage: snapshot.categories?.preciousMetals?.percentage || 0,
          count: snapshot.categories?.preciousMetals?.count || 0,
          lastUpdated: snapshot.categories?.preciousMetals?.lastUpdated || null,
        },
        cash: {
          value: parseFloat(formData.cash) || 0,
          percentage: snapshot.categories?.cash?.percentage || 0,
          count: snapshot.categories?.cash?.count || 0,
          lastUpdated: snapshot.categories?.cash?.lastUpdated || null,
        },
        realEstate: {
          value: parseFloat(formData.realEstate) || 0,
          percentage: snapshot.categories?.realEstate?.percentage || 0,
          count: snapshot.categories?.realEstate?.count || 0,
          lastUpdated: snapshot.categories?.realEstate?.lastUpdated || null,
        },
        liabilities: {
          value: parseFloat(formData.liabilities) || 0,
          percentage: snapshot.categories?.liabilities?.percentage || 0,
          count: snapshot.categories?.liabilities?.count || 0,
          lastUpdated: snapshot.categories?.liabilities?.lastUpdated || null,
        },
      };

      // Recalculate percentages based on total assets
      const totalAssets = parseFloat(formData.totalAssets) || 0;
      if (totalAssets > 0) {
        Object.keys(categories).forEach(key => {
          if (key !== 'liabilities') {
            categories[key].percentage = parseFloat(
              ((categories[key].value / totalAssets) * 100).toFixed(2)
            );
          }
        });
      }

      const updateData = {
        totalValue: parseFloat(formData.totalValue) || 0,
        totalAssets: parseFloat(formData.totalAssets) || 0,
        totalLiabilities: parseFloat(formData.totalLiabilities) || 0,
        netWorth: parseFloat(formData.netWorth) || 0,
        currency: formData.currency,
        categories,
        containsStaleData: formData.containsStaleData,
        staleCategories: formData.staleCategories,
        sp500Value: formData.sp500Value !== '' ? parseFloat(formData.sp500Value) : null,
      };

      await onSave(formData.date, updateData);
    } catch (error) {
      console.error('Error saving snapshot:', error);
      setErrors({ submit: error.message || 'Failed to save snapshot' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content snapshot-edit-form" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Snapshot</h2>
        <p className="snapshot-date">Date: <strong>{formData.date}</strong></p>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Portfolio Totals</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="totalValue">Total Value (Legacy)</label>
                <input
                  type="number"
                  id="totalValue"
                  name="totalValue"
                  value={formData.totalValue}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.totalValue && <span className="error">{errors.totalValue}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="totalAssets">Total Assets</label>
                <input
                  type="number"
                  id="totalAssets"
                  name="totalAssets"
                  value={formData.totalAssets}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.totalAssets && <span className="error">{errors.totalAssets}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="totalLiabilities">Total Liabilities</label>
                <input
                  type="number"
                  id="totalLiabilities"
                  name="totalLiabilities"
                  value={formData.totalLiabilities}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.totalLiabilities && <span className="error">{errors.totalLiabilities}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="netWorth">Net Worth</label>
                <input
                  type="number"
                  id="netWorth"
                  name="netWorth"
                  value={formData.netWorth}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.netWorth && <span className="error">{errors.netWorth}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Category Breakdown</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="stocks">Stocks</label>
                <input
                  type="number"
                  id="stocks"
                  name="stocks"
                  value={formData.stocks}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.stocks && <span className="error">{errors.stocks}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="crypto">Crypto</label>
                <input
                  type="number"
                  id="crypto"
                  name="crypto"
                  value={formData.crypto}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.crypto && <span className="error">{errors.crypto}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="preciousMetals">Precious Metals</label>
                <input
                  type="number"
                  id="preciousMetals"
                  name="preciousMetals"
                  value={formData.preciousMetals}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.preciousMetals && <span className="error">{errors.preciousMetals}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="cash">Cash</label>
                <input
                  type="number"
                  id="cash"
                  name="cash"
                  value={formData.cash}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.cash && <span className="error">{errors.cash}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="realEstate">Real Estate</label>
                <input
                  type="number"
                  id="realEstate"
                  name="realEstate"
                  value={formData.realEstate}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.realEstate && <span className="error">{errors.realEstate}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="liabilities">Liabilities</label>
                <input
                  type="number"
                  id="liabilities"
                  name="liabilities"
                  value={formData.liabilities}
                  onChange={handleChange}
                  step="0.01"
                />
                {errors.liabilities && <span className="error">{errors.liabilities}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Benchmark Data</h3>
            <div className="form-group">
              <label htmlFor="sp500Value">S&P 500 Index Value</label>
              <input
                type="number"
                id="sp500Value"
                name="sp500Value"
                value={formData.sp500Value}
                onChange={handleChange}
                step="0.01"
                placeholder="e.g., 5917.25"
              />
              {errors.sp500Value && <span className="error">{errors.sp500Value}</span>}
              <small className="field-hint">The S&P 500 index value on this date (for portfolio comparison)</small>
            </div>
          </div>

          <div className="form-section">
            <h3>Metadata</h3>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="containsStaleData"
                  checked={formData.containsStaleData}
                  onChange={handleChange}
                />
                Contains Stale Data
              </label>
            </div>

            {formData.containsStaleData && (
              <div className="info-box">
                <strong>Stale Categories:</strong> {formData.staleCategories.join(', ') || 'None'}
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="error-message">{errors.submit}</div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-secondary" disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SnapshotEditForm;
